import {
  Space,
  Skeleton,
  Popconfirm,
  message,
  Popover,
  Spin,
  Input,
} from 'antd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/lib/table';
import { useState, useEffect, useRef, useCallback } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { PlusOutlined } from '@ant-design/icons';
import { useDebounce } from 'use-debounce';

import { INVOICE_JOBS, JOB_NUMBERS } from 'events/types';
import AddNewJobNumberForm from 'pages/Dialogs/AddNewJobNumberForm';
import { deleteJob, getMyInvoiceJobs } from 'services/jobs';
import styled from 'styled-components';
import { toastError } from 'services/helpers/getError';

import useTranslation from '../../customHooks/translate';
import BasicTable from '../../components/table/basic';

const JobNumbers = () => {
  const [addJobNumberFormOpen, setAddJobNumberFormOpen] = useState(false);
  const [jobNumberToUpdate, setJobNumberToUpdate] = useState(null);
  const [pageSize] = useState(15);
  const [pageIndex, setPageIndex] = useState(0);
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const queryClient = useQueryClient();
  const [isMobile, setIsMobile] = useState(false);
  const [searchText, setSearchText] = useState('');

  const isComponentMounted = useRef(false);
  const [debouncedSearchText] = useDebounce(searchText, 800);
  const translation = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch data function
  const fetchData = useCallback(
    async (
      currentPage: number,
      isNewRequest: boolean = false,
      searchTextFilter?: string
    ) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const response = await getMyInvoiceJobs(
          pageSize,
          currentPage,
          searchTextFilter
        );

        const data = response?.data?.data ?? [];
        const totalRecord = response?.data?.totalRecord ?? 0;

        // Ensure we have data to work with
        if (data.length) {
          const newData = data.map((item: any) => ({
            ...item,
            key: item.id,
          }));

          setTotalCount(totalRecord);

          // Update the data state
          if (isNewRequest) {
            setTableData(newData);
          } else {
            setTableData((prev) => [...prev, ...newData]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : tableData.length) + newData.length;
          const hasMoreItems = newTotalItems < totalRecord;
          setHasMore(hasMoreItems);

          return response.data;
        }

        if (isNewRequest) {
          setTableData([]);
          setTotalCount(totalRecord);
          setHasMore(false);
        }
        return null;
      } catch (error: any) {
        message.error(error?.response?.data.Message);
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [pageSize, tableData.length]
  );

  const resetData = () => {
    setTableData([]);
    setPageIndex(0);
    setHasMore(true);
  };

  // Initial data fetch
  const { isLoading, refetch } = useQuery(
    [INVOICE_JOBS, debouncedSearchText],
    () => fetchData(0, true, debouncedSearchText),
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      enabled: true,
      staleTime: 0,
      cacheTime: 60000,
    }
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isComponentMounted.current) {
        resetData();
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);

  useEffect(() => {
    isComponentMounted.current = true;
    resetData();
    refetch();
    return () => {
      isComponentMounted.current = false;
    };
  }, [refetch]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (isComponentMounted.current) {
        if (tableData.length === 0) {
          refetch();
        }
      }
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [refetch, tableData.length]);

  // Handle infinite scrolling
  const handleScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMore ||
      isLoading
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchData(nextPage, false, debouncedSearchText);
    }
  }, [pageIndex, hasMore, isLoading, fetchData, debouncedSearchText]);

  // Set up scroll event listener
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  const columns: ColumnsType<any> = [
    {
      title: <span>{translation.InvoiceDetails.JobNumber}</span>,
      dataIndex: 'jobId',
      key: 'jobId',
      align: 'center',
      width: 100,
    },
    {
      title: <span>{translation.action.Action}</span>,
      dataIndex: 'actions',
      align: 'center',
      key: 'actions',
      width: 100,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EditDeleteIcon
                  src="assets/edit-icon.svg"
                  alt="Edit"
                  onClick={() => {
                    setJobNumberToUpdate(record);
                    setAddJobNumberFormOpen(true);
                  }}
                />
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            <div style={{ cursor: 'pointer' }}>
              <img
                className="edit"
                src="assets/edit-icon.svg"
                alt="Edit"
                onClick={() => {
                  setJobNumberToUpdate(record);
                  setAddJobNumberFormOpen(true);
                }}
              />
            </div>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <div style={{ cursor: 'pointer' }}>
                <img
                  className="delete"
                  src="assets/delete-icon.svg"
                  alt="Delete"
                />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  const handleDelete = async (record) => {
    try {
      await deleteJob(record.id);
      message.success(`${translation.messages.successDeleted}`);
      resetData();
      refetch();
      queryClient.invalidateQueries({
        queryKey: [INVOICE_JOBS],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: [JOB_NUMBERS],
        refetchType: 'all',
      });
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <>
      <div className="mb-[20px] flex w-full flex-wrap items-center justify-between gap-[10px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.jobNumbers.LabelText}
        </p>
        <Input
          onChange={(event) => setSearchText(event?.target?.value)}
          value={searchText}
          className="max-h-10 w-full rounded-[8px] border-none bg-[#F6F6F6] text-[#1D1D1D] transition-all duration-300 ease-in-out hover:bg-gray-100 sm:w-[280px]"
          placeholder={translation.jobNumbers.LabelText}
        />
        <button
          onClick={() => setAddJobNumberFormOpen(true)}
          className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
        >
          <PlusOutlined />
          <p className="hidden md:block">{translation.jobNumbers.Header}</p>
        </button>
      </div>

      <AddNewJobNumberForm
        open={addJobNumberFormOpen}
        jobNumberToUpdate={jobNumberToUpdate}
        handleClose={() => {
          setAddJobNumberFormOpen(false);
          resetData();
          refetch();
        }}
      />
      <STableWrapper>
        {isLoading && tableData.length === 0 ? (
          <Skeleton active />
        ) : (
          <ScrollContainer ref={tableContainerRef}>
            <BasicTable
              sticky={{
                offsetHeader: 0,
              }}
              className="custom-bordered"
              bordered
              showHeader={true}
              data={tableData}
              columns={columns}
            />
            <LoadingIndicator ref={loadingRef}>
              {isLoadingMore && <Spin size="small" />}
              {!hasMore && tableData.length > 0 && (
                <div style={{ color: '#999', marginTop: '8px' }}>
                  {translation.messages.allData}
                </div>
              )}
            </LoadingIndicator>
          </ScrollContainer>
        )}
      </STableWrapper>
    </>
  );
};

const STableWrapper = styled.div`
  margin-top: 24px;
  height: 500px;
  overflow: auto;
`;

export const SearchIcon = styled(MagnifyingGlass)`
  position: absolute;
  top: 10px;
  right: 10px;
  color: #466afb;
  font-size: 20px;
`;

export const SearchWrapper = styled.div`
  position: relative;
`;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

// Styled scroll container
const ScrollContainer = styled.div`
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  position: relative;
`;

// Styled loading indicator
const LoadingIndicator = styled.div`
  text-align: center;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
`;

export default JobNumbers;
